import type { Filter, ProjectStatus } from "interfaces/project/project.types";
import type { Translations } from "interfaces/shared.types";

interface FilterUpdateOptions {
  checkingWord?: string;
  id: string;
}

interface OverallStatistics {
  notShared?: number;
  shared?: number;
  statuses?: Record<string, number>;
}

const handleProjectListFilterUpdate = (
  filter: Filter,
  { checkingWord, id }: FilterUpdateOptions,
): Filter => {
  if (
    (filter.id === "all-projects" && filter.isActive) ||
    (checkingWord && checkingWord === filter.name)
  ) {
    filter.isActive = false;
  }
  if (filter.id === id) {
    filter.isActive = !filter.isActive;
  }
  return filter;
};

export const handleProjectListFiltersUpdate = (
  prevFilters: Filter[],
  options: FilterUpdateOptions,
): Filter[] => {
  const { id } = options;
  const isIdActive = prevFilters.some(
    (filter) => filter.id === id && filter.isActive,
  );

  const updatedFilters = prevFilters.map((filter) => {
    const updatedFilter = handleProjectListFilterUpdate(filter, options);

    if (filter.id === "all-projects" && !isIdActive && updatedFilter.isActive) {
      updatedFilter.isActive = false;
    }

    return updatedFilter;
  });

  const noOtherFilterActive = updatedFilters.every(
    (filter) => filter.id === "all-projects" || !filter.isActive,
  );

  if (noOtherFilterActive) {
    const allProjectsFilter = updatedFilters.find(
      (filter) => filter.id === "all-projects",
    );
    if (allProjectsFilter) {
      allProjectsFilter.isActive = true;
    }
  }
  return updatedFilters;
};

export const activateFilter = (
  filters: Filter[],
  activeId: string,
): Filter[] => {
  const res = filters.map((filter) => ({
    ...filter,
    isActive: filter.id === activeId,
  }));
  return res;
};

export const updateProjectFilters = (
  translations: Translations,
  overallStatistics: OverallStatistics | null | undefined,
  projectsCount: number,
  setSearchQuery: (query: null) => void,
  setSharedStatusSwitcher: (
    callback: (prev: boolean | null) => boolean | null,
  ) => void,
  setFilters: (callback: (prevFilters: Filter[]) => Filter[]) => void,
  projectStatuses: ProjectStatus[],
): Filter[] => {
  const newFilters: Filter[] = [
    {
      id: "all-projects",
      name: translations.allProjects,
      count: projectsCount,
      onClick: () => {
        setSearchQuery(null);
        setSharedStatusSwitcher(() => null);
        setFilters((prevFilters) =>
          activateFilter(prevFilters, "all-projects"),
        );
      },
      isActive: true,
    },
  ];

  if (overallStatistics && Object.keys(overallStatistics).length) {
    newFilters.push({
      id: "not-shared",
      name: translations.notShared,
      count: overallStatistics.notShared,
      icon: "user-regular",
      disabled: true,
      onClick: () => {
        setSharedStatusSwitcher((prev) =>
          prev === null || prev ? false : null,
        );
        setFilters((prevFilters) =>
          handleProjectListFiltersUpdate(prevFilters, {
            checkingWord: translations.shared,
            id: "not-shared",
          }),
        );
      },
    });

    newFilters.push({
      id: "shared",
      name: translations.shared,
      count: overallStatistics.shared,
      icon: "user-group-regular",
      disabled: true,
      onClick: () => {
        setSharedStatusSwitcher((prev) =>
          prev === null || !prev ? true : null,
        );
        setFilters((prevFilters) =>
          handleProjectListFiltersUpdate(prevFilters, {
            checkingWord: translations.notShared,
            id: "shared",
          }),
        );
      },
    });

    projectStatuses.forEach(({ status, color }) => {
      newFilters.push({
        id: status,
        name: status,
        count: overallStatistics?.statuses?.[status],
        color,
        isActive: false,
        isStatusFilter: true,
      });
    });
  }

  return newFilters;
};
